import { useEffect, useMemo } from "react";
import { TriangleAlert } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { ScreenShell } from "../components/ScreenShell";
import { AnimatedRing } from "../components/AnimatedRing";
import { BmiCard } from "../components/BmiCard";
import type { FlowAnswers } from "../types";
import { kgToDisplay } from "../types";
import { computePlan } from "../plan";
import type { OnboardingPlan } from "../plan";
import type { BmiClass } from "../../../convex/lib/engine";

interface ResultsScreenProps {
  readonly answers: FlowAnswers;
  readonly onContinue: () => void;
}

const BMI_CLASSES: readonly BmiClass[] = ["underweight", "healthy", "overweight", "obesity"];

function asBmiClass(value: string | undefined): BmiClass {
  return BMI_CLASSES.find((entry) => entry === value) ?? "healthy";
}

function goalSummary(answers: FlowAnswers): string {
  const delta =
    answers.weightKg !== undefined && answers.targetWeightKg !== undefined
      ? Math.round(kgToDisplay(Math.abs(answers.targetWeightKg - answers.weightKg), answers.unitWeight))
      : undefined;
  const unit = answers.unitWeight;
  switch (answers.goalType) {
    case "lose":
      return delta !== undefined && delta > 0 ? `Lose ${delta} ${unit}` : "Lose weight";
    case "gain":
    case "muscle":
      return delta !== undefined && delta > 0 ? `Gain ${delta} ${unit}` : "Gain weight";
    case "maintain":
      return "Maintain weight";
    default:
      return "Eat healthier";
  }
}

interface StatCardProps {
  readonly label: string;
  readonly value: string;
}

function StatCard({ label, value }: StatCardProps) {
  return (
    <div className="flex flex-col gap-1 rounded-2xl border border-border bg-surface p-4">
      <span className="text-xs font-medium uppercase tracking-wide text-text-muted">{label}</span>
      <span className="text-base font-bold text-text">{value}</span>
    </div>
  );
}

function formatDayMonth(epochMs: number): string {
  return new Date(epochMs).toLocaleDateString(undefined, { day: "numeric", month: "short" });
}

function timelineSummary(plan: OnboardingPlan): string | null {
  if (
    plan.weeksToGoal <= 0 ||
    plan.weeksMin === undefined ||
    plan.weeksMax === undefined ||
    plan.weeksMax === 0
  ) {
    return null;
  }
  const min = Math.max(1, plan.weeksMin);
  const max = Math.max(min, plan.weeksMax);
  return min === max
    ? `approximately ${min} weeks`
    : `approximately ${min}–${max} weeks`;
}

function goalDateSummary(plan: OnboardingPlan): string | null {
  if (plan.goalDateMin === undefined || plan.goalDateMax === undefined) return null;
  if (plan.weeksToGoal <= 0 || plan.weeksMax === 0) return null;
  const min = formatDayMonth(plan.goalDateMin);
  const max = formatDayMonth(plan.goalDateMax);
  return min === max ? `around ${min}` : `between ${min} and ${max}`;
}

function weeklyRateSummary(plan: OnboardingPlan, weightKg?: number): string {
  if (plan.weeklyRateKg <= 0) return "At your own pace";
  const percent =
    weightKg !== undefined && weightKg > 0
      ? Math.round((plan.weeklyRateKg / weightKg) * 1000) / 10
      : undefined;
  return percent !== undefined
    ? `${percent}%/week (${plan.weeklyRateKg} kg)`
    : `${plan.weeklyRateKg} kg/week`;
}

function RestrictedPanel({ answers, plan }: { readonly answers: FlowAnswers; readonly plan: OnboardingPlan }) {
  const underweightLoss =
    answers.goalType === "lose" && plan.bmi !== undefined && plan.bmi < 18.5;
  return (
    <div
      data-testid="restricted-plan-panel"
      className="flex w-full flex-col gap-3 rounded-2xl border border-warning/40 bg-warning/10 p-4"
    >
      <div className="flex items-center gap-2">
        <TriangleAlert size={18} className="shrink-0 text-warning" aria-hidden="true" />
        <span className="text-sm font-semibold text-text">A safer starting point</span>
      </div>
      <p className="text-sm leading-relaxed text-text">
        {underweightLoss
          ? "Automated weight-loss programmes aren't available below the healthy BMI range. We've set a maintenance plan — please consider professional guidance."
          : "Based on your answers, automated calorie deficits aren't the right fit right now. We've set a maintenance plan — please consider professional guidance for an individual programme."}
      </p>
    </div>
  );
}

export function ResultsScreen({ answers, onContinue }: ResultsScreenProps) {
  const plan = useMemo(
    () =>
      computePlan({
        goalType: answers.goalType,
        weightKg: answers.weightKg,
        targetWeightKg: answers.targetWeightKg,
        heightCm: answers.heightCm,
        age: answers.age,
        gender: answers.gender,
        activityLevel: answers.activityLevel,
        exerciseFrequency: answers.exerciseFrequency,
        exerciseType: answers.exerciseType,
        avgDailySteps: answers.avgDailySteps,
        weightLossPace: answers.weightLossPace,
        eatingStyle: answers.eatingStyle,
        bodyFatPercent: answers.bodyFatPercent,
        waistCm: answers.waistCm,
        safetyFlags: answers.safetyFlags,
      }),
    [answers],
  );

  useEffect(() => {
    console.log("[Frontend][OnboardingFlow] Plan computed", {
      dailyKcal: plan.dailyKcal,
      weeksToGoal: plan.weeksToGoal,
      weeksMin: plan.weeksMin ?? null,
      weeksMax: plan.weeksMax ?? null,
      restricted: plan.restricted === true,
      wasConstrained: plan.wasConstrained === true,
      dietType: plan.dietType,
    });
  }, [plan]);

  const restricted = plan.restricted === true;
  const timeline = timelineSummary(plan);
  const goalDates = goalDateSummary(plan);

  return (
    <ScreenShell
      title="Your plan is ready"
      footer={
        <Button variant="primary" size="lg" className="w-full" onClick={onContinue}>
          See Full Plan
        </Button>
      }
    >
      <div className="flex flex-col items-center gap-6">
        <AnimatedRing
          value={plan.dailyKcal}
          centerLabel="kcal / day"
          sublabel={restricted ? "maintenance target" : "initial daily target"}
        />
        {plan.bmi !== undefined && (
          <BmiCard bmi={plan.bmi} classification={asBmiClass(plan.bmiClass)} />
        )}
        {restricted ? (
          <RestrictedPanel answers={answers} plan={plan} />
        ) : (
          <div className="grid w-full grid-cols-2 gap-3">
            <StatCard label="Protein target" value={`${plan.proteinG} g/day`} />
            <StatCard label="Goal" value={goalSummary(answers)} />
            <StatCard label="Pace" value={weeklyRateSummary(plan, answers.weightKg)} />
            <StatCard
              label="Timeline"
              value={timeline !== null ? timeline : "Stay consistent to feel your best"}
            />
          </div>
        )}
        {!restricted && timeline !== null && (
          <div className="flex w-full flex-col gap-1 rounded-2xl border border-border bg-surface p-4">
            <p className="text-sm leading-relaxed text-text">
              Your estimated timeline is {timeline}
              {goalDates !== null ? ` — reaching your goal ${goalDates}` : ""}.
            </p>
            <p className="text-xs leading-relaxed text-text-muted">
              This estimate updates as your real food and weight data come in.
            </p>
          </div>
        )}
        {!restricted && plan.wasConstrained === true && (
          <p className="text-center text-sm leading-relaxed text-text-muted">
            We adjusted your plan to keep it safe — your timeline was extended instead of cutting
            calories further.
          </p>
        )}
        <p className="text-center text-sm text-text-muted">
          This plan is designed to be sustainable, not extreme.
        </p>
      </div>
    </ScreenShell>
  );
}
